import { IAccountsPayableOpo, IAccountsPayableOpoDetail } from "@nkr/models/reports";
import { Button, Col, FormControl, FormGroup, InputGroup, Modal, Row } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import { DetailReportWrapper } from "../accounts-payable/styles";
import { useMemo, useState } from "react";
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, ValueGetterParams } from "ag-grid-community";
import SortableHeaderComponent from "@nkr/views/admin/center/list/common/sortable-header";
import useOpoDetails from "@nkr/store/reports/hooks/use-opo-details";
import { ISemesterPayment } from "@nkr/store/reports/hooks/use-opo-payments";
import RankRenderer from "@nkr/views/admin/center/list/common/rank-renderer";
import * as moment from 'moment';
import { styles } from './styles';

type TProps = {
    ap: IAccountsPayableOpo,
    onMarkPaid: (achConfirmation: string, semesters: ISemesterPayment[]) => Promise<void>
};

export default function DetailsModalOpo({ ap, onMarkPaid }: TProps) {
    const [show, setShow] = useState<boolean>(false);
    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [achNumber, setAchNumber] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const { list, isPayable } = useOpoDetails(ap, show);

    const sum = list.reduce((acc, item) => acc + item.amount, 0);
    const valid = isPayable ? !!ap.confirmationNumber : achNumber.length > 0;

    const columns = useMemo((): ColDef[] => [
        {
            colId: 'rank',
            width: 50,
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.node.rowPinned) return 'Totals:';
                return <RankRenderer {...params} />;
            },
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            width: 130,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient</SortableHeaderComponent>,
        },
        {
            colId: 'invoiceDate',
            field: 'invoiceDate',
            width: 110,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Invoice Date</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.node.rowPinned) return '';
                return moment(params.data.invoiceDate).isValid() ? moment(params.data.invoiceDate).format('MM/DD/YYYY') : '--';
            },
        },
        {
            colId: 'daysOutstanding',
            field: 'daysOutstanding',
            width: 80,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Days O/S</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.node.rowPinned) return params.value != null ? '~' + params.value : '';
                return params.value;
            },
        },
        {
            colId: 'amount',
            field: 'amount',
            width: 100,
            sortingOrder: ['desc', 'asc'],
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Amount</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) =>
                params.data.amount.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        },
        {
            colId: 'isPaid',
            field: 'isPaid',
            width: 70,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Paid</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.node.rowPinned) return '';
                return params.data.isPaid ? <span className='label label-success'>Y</span> : '-';
            },
        },
    ], []);

    const onGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);
    };

    const handleMarkPaid = async () => {
        const semesterMap = list.reduce((acc, item) => {
            const date = moment(item.invoiceDate);
            const year = date.year();
            const semester = date.month() < 6 ? 1 : 2;
            const key = `${year}-${semester}`;
            if (!acc[key]) acc[key] = { opoId: item.opoId, year, semester, amount: 0 };
            acc[key].amount += item.amount;
            return acc;
        }, {} as Record<string, ISemesterPayment>);

        setIsSubmitting(true);
        try {
            await onMarkPaid(isPayable ? ap.confirmationNumber : achNumber, Object.values(semesterMap));
            setShow(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setShow(false);
    };

    return (<>
        <Button bsSize="xs" bsStyle={isPayable ? 'success' : 'primary'} onClick={() => setShow(true)}>
            {isPayable ? 'Pay' : 'Details'}
        </Button>

        <Modal backdrop='static' className='modal-xxl' bsSize='lg' show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Payable OPO: {ap.opoAlias}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <DetailReportWrapper style={{ height: 500 }}>
                    <AgGridReact
                        defaultColDef={{
                            headerClass: 'list-header',
                            cellClass: 'list-cell',
                            sortable: true,
                        }}
                        columnDefs={columns}
                        rowData={list}
                        suppressRowClickSelection={true}
                        suppressCellSelection={true}
                        suppressContextMenu={true}
                        pinnedTopRowData={[{
                            amount: list.reduce((a, v) => a + v.amount, 0),
                            daysOutstanding: list.length > 0 ? Math.round(list.reduce((a, v) => a + v.daysOutstanding, 0) / list.length) : null
                        }]}
                        noRowsOverlayComponentFramework={() => <div>No records found...</div>}
                        onGridReady={onGridReady}
                        onFirstDataRendered={() => gridApi.sizeColumnsToFit()}
                        onRowDataChanged={() => gridApi && gridApi.sizeColumnsToFit()}
                        onSortChanged={() => gridApi.refreshCells()}
                        getRowStyle={(params: any) => {
                            if (params.node.rowPinned) {
                                return { backgroundColor: 'lightblue', fontWeight: 700 } as any;
                            }
                        }}
                    />
                </DetailReportWrapper>

                <Row style={styles.payFormRow}>
                    <Col xs={4}>
                        <FormGroup className="form-inline">
                            <label>Amount: &nbsp;</label>
                            <InputGroup>
                                <InputGroup.Addon>$</InputGroup.Addon>
                                <FormControl type="text" value={sum.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} readOnly />
                            </InputGroup>
                        </FormGroup>
                    </Col>
                    <Col xs={4}>
                        <FormGroup className="form-inline">
                            <label>ACH Confirmation #: &nbsp;</label>
                            {isPayable
                                ? <InputGroup><FormControl type="text" value={ap.confirmationNumber} disabled /></InputGroup>
                                : <InputGroup><FormControl type="text" value={achNumber} onChange={(e: React.FormEvent<FormControl>) => setAchNumber((e.target as HTMLInputElement).value)} /></InputGroup>
                            }
                        </FormGroup>
                    </Col>
                    <Col xs={4}>
                        <Button
                            className={valid ? 'btn btn-primary' : 'btn btn-default'}
                            disabled={!valid || isSubmitting}
                            onClick={handleMarkPaid}
                        >
                            {isSubmitting ? 'Saving...' : 'Mark Paid'}
                        </Button>
                    </Col>
                </Row>
            </Modal.Body>

            <Modal.Footer>
                <Button onClick={handleClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    </>);
}
